import React, {
  useState,
  useMemo,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';

import { pagesConfig } from '../../config/pages';
import captureStorageError from '../../../utils/localStorage';

import NavigationContext from '../../context/NavigationContext';

import LayoutContext, {
  initialValues,
  DEFAULT_RIGHT_PANEL_WIDTH,
  MIN_RIGHT_PANEL_WIDTH,
  MAX_RIGHT_PANEL_WIDTH,
  STORAGE_KEYS,
} from './LayoutContext';

const getStoredRightPanelWidth = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.RIGHT_PANEL_WIDTH);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!Number.isNaN(parsed) && parsed >= MIN_RIGHT_PANEL_WIDTH && parsed <= MAX_RIGHT_PANEL_WIDTH) {
        return parsed;
      }
    }
  } catch (e) {
    captureStorageError('getItem', STORAGE_KEYS.RIGHT_PANEL_WIDTH, e, 'CoachTools layout');
  }
  return DEFAULT_RIGHT_PANEL_WIDTH;
};

const getStoredChatPanelOpen = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CHAT_PANEL_OPEN);
    if (stored !== null) {
      return stored === 'true';
    }
  } catch (e) {
    captureStorageError('getItem', STORAGE_KEYS.CHAT_PANEL_OPEN, e, 'CoachTools layout');
  }
  return true; // default to open
};

const LayoutContextProvider = ({ children }) => {
  const [title, setTitle] = useState(initialValues.title);
  const [isDrawerOpen, setDrawerOpen] = useState(initialValues.isDrawerOpen);
  const [rightPanelWidth, setRightPanelWidthState] = useState(getStoredRightPanelWidth);
  const [rightPanelContent, setRightPanelContentState] = useState(null);
  const [isChatPanelOpen, setIsChatPanelOpenState] = useState(getStoredChatPanelOpen);
  const { activePage } = useContext(NavigationContext);

  useEffect(() => {
    if (activePage) {
      setTitle(pagesConfig[activePage].title || '');
    }
  }, [activePage]);

  const toggleDrawer = useCallback(() => {
    setDrawerOpen((isOpen) => !isOpen);
  }, [
    setDrawerOpen,
  ]);

  const setRightPanelWidth = useCallback((width, options = {}) => {
    setRightPanelWidthState(width);
    const { persist = true } = options;
    if (!persist) return;
    try {
      localStorage.setItem(STORAGE_KEYS.RIGHT_PANEL_WIDTH, String(width));
    } catch (e) {
      captureStorageError('setItem', STORAGE_KEYS.RIGHT_PANEL_WIDTH, e, 'CoachTools layout');
    }
  }, []);

  const setIsChatPanelOpen = useCallback((isOpen) => {
    setIsChatPanelOpenState((prevValue) => {
      const newValue = typeof isOpen === 'function' ? isOpen(prevValue) : isOpen;
      try {
        localStorage.setItem(STORAGE_KEYS.CHAT_PANEL_OPEN, String(newValue));
      } catch (e) {
        captureStorageError('setItem', STORAGE_KEYS.CHAT_PANEL_OPEN, e, 'CoachTools layout');
      }
      return newValue;
    });
  }, []);

  const setRightPanelContent = useCallback((content, options = {}) => {
    setRightPanelContentState(content);
    const { openPanel = true } = options;
    // Custom content can optionally open the panel; default behavior preserves previous behavior.
    if (content && openPanel) {
      setIsChatPanelOpen(true);
    }
  }, [setIsChatPanelOpen]);

  // hasRightPanel is true when custom content is set (e.g., Activity Feed page)
  const hasRightPanel = !!rightPanelContent;

  const contextValue = useMemo(() => ({
    title,
    isDrawerOpen,
    toggleDrawer,
    rightPanelWidth,
    setRightPanelWidth,
    rightPanelContent,
    setRightPanelContent,
    isChatPanelOpen,
    setIsChatPanelOpen,
    hasRightPanel,
  }), [
    title,
    isDrawerOpen,
    toggleDrawer,
    rightPanelWidth,
    setRightPanelWidth,
    rightPanelContent,
    setRightPanelContent,
    isChatPanelOpen,
    setIsChatPanelOpen,
    hasRightPanel,
  ]);

  return (
    <LayoutContext.Provider value={contextValue}>
      {children}
    </LayoutContext.Provider>
  );
};

LayoutContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default LayoutContextProvider;
